import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { quizQuestions, quizResults, scoreMap, QuizResult } from "@/mocks/quiz";
import { products } from "@/mocks/products";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Phase = "intro" | "question" | "result";

export default function EnergyQuizModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("intro");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPhase("intro");
      setCurrentQ(0);
      setAnswers([]);
      setSelected(null);
      setResult(null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const totalQ = quizQuestions.length;
  const progress = phase === "result" ? 100 : (currentQ / totalQ) * 100;

  const handleStart = () => {
    setPhase("question");
    setCurrentQ(0);
    setSelected(null);
  };

  const handleSelect = (value: string) => {
    setSelected(value);
  };

  const handleNext = () => {
    if (!selected) return;
    const newAnswers = [...answers, selected];

    if (currentQ < totalQ - 1) {
      setAnimating(true);
      setTimeout(() => {
        setAnswers(newAnswers);
        setCurrentQ(currentQ + 1);
        setSelected(null);
        setAnimating(false);
      }, 300);
    } else {
      // Calculate result
      const scores: Record<string, number> = { dzi: 0, crystal: 0, jade: 0, thangka: 0 };
      newAnswers.forEach((ans) => {
        const mapping = scoreMap[ans];
        if (mapping) {
          Object.keys(mapping).forEach((cat) => {
            scores[cat] = (scores[cat] || 0) + mapping[cat];
          });
        }
      });
      const topCategory = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
      const found = quizResults.find((r) => r.id === topCategory) || quizResults[0];
      setResult(found);
      setAnimating(true);
      setTimeout(() => {
        setPhase("result");
        setAnimating(false);
      }, 400);
    }
  };

  const handleRetake = () => {
    setPhase("intro");
    setCurrentQ(0);
    setAnswers([]);
    setSelected(null);
    setResult(null);
  };

  const handleViewProducts = () => {
    if (result) {
      onClose();
      navigate(`/products?category=${result.productCategory}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const question = quizQuestions[currentQ];
  const recommendedProducts = result
    ? products.filter((p) => result.productIds.includes(p.id))
    : [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: phase === "result" && result ? result.bgColor : "#fdf8f2" }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg text-[#5a4e45]"></i>
        </button>

        {/* Progress bar */}
        {phase === "question" && (
          <div className="absolute top-0 left-0 w-full h-1 bg-[#e8e0d5] rounded-t-2xl overflow-hidden">
            <div
              className="h-full bg-[#8b6f47] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* ── INTRO ── */}
        {phase === "intro" && (
          <div className="p-8 md:p-12 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#f5ede0]">
              <i className="ri-sparkling-2-line text-4xl text-[#8b6f47]"></i>
            </div>
            <h2
              className="text-2xl md:text-3xl font-bold text-[#3d3530] mb-3"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              能量飾品測驗
            </h2>
            <p className="text-[#8b7d72] text-base mb-2" style={{ fontFamily: "'Noto Serif TC', serif" }}>
              透過 5 個問題，找到最適合你的能量飾品
            </p>
            <p className="text-[#a89880] text-sm mb-8">
              每一件飾品都有其獨特的能量頻率，讓我們幫你找到最契合的那一件
            </p>

            {/* Preview categories */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {[
                { icon: "ri-ancient-gate-line", label: "西藏天珠", color: "#8b6f47", bg: "#f5ede0" },
                { icon: "ri-sparkling-2-line", label: "天然水晶", color: "#7a6a9a", bg: "#f0edf7" },
                { icon: "ri-leaf-line", label: "翡翠玉石", color: "#4a7a52", bg: "#eaf2eb" },
                { icon: "ri-shield-star-line", label: "唐卡佛牌", color: "#c9a96e", bg: "#faf5ec" },
              ].map((cat) => (
                <div
                  key={cat.label}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl"
                  style={{ background: cat.bg }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: cat.color + "20" }}>
                    <i className={`${cat.icon} text-xl`} style={{ color: cat.color }}></i>
                  </div>
                  <span className="text-xs font-medium text-[#5a4e45]" style={{ fontFamily: "'Noto Serif TC', serif" }}>
                    {cat.label}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={handleStart}
              className="w-full md:w-auto px-10 py-4 bg-[#8b6f47] text-white rounded-full text-base font-semibold cursor-pointer hover:bg-[#7a5f3a] transition-all duration-300 whitespace-nowrap"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              開始測驗
            </button>
            <p className="text-xs text-[#b0a090] mt-3">約需 1 分鐘完成</p>
          </div>
        )}

        {/* ── QUESTION ── */}
        {phase === "question" && question && (
          <div className={`p-8 md:p-10 transition-opacity duration-300 ${animating ? "opacity-0" : "opacity-100"}`}>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
              {quizQuestions.map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 flex-1 rounded-full transition-all duration-300"
                  style={{ background: i <= currentQ ? "#8b6f47" : "#e8e0d5" }}
                />
              ))}
            </div>

            <p className="text-xs text-[#a89880] mb-2 tracking-widest uppercase">
              第 {currentQ + 1} 題 / 共 {totalQ} 題
            </p>
            <h3
              className="text-xl md:text-2xl font-bold text-[#3d3530] mb-1"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              {question.question}
            </h3>
            <p className="text-sm text-[#a89880] mb-6">{question.subtitle}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {question.options.map((opt) => {
                const isSelected = selected === opt.value;
                return (
                  <button
                    key={opt.id}
                    onClick={() => handleSelect(opt.value)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left cursor-pointer transition-all duration-200 whitespace-normal ${
                      isSelected
                        ? "border-[#8b6f47] bg-[#f5ede0]"
                        : "border-[#e8e0d5] bg-white hover:border-[#c9a96e] hover:bg-[#fdf8f2]"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                        isSelected ? "bg-[#8b6f47]" : "bg-[#f5ede0]"
                      }`}
                    >
                      <i
                        className={`${opt.icon} text-lg transition-colors duration-200 ${
                          isSelected ? "text-white" : "text-[#8b6f47]"
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`text-sm font-medium transition-colors duration-200 ${
                        isSelected ? "text-[#8b6f47]" : "text-[#5a4e45]"
                      }`}
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      {opt.label}
                    </span>
                    {isSelected && (
                      <i className="ri-check-line text-[#8b6f47] ml-auto flex-shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleNext}
              disabled={!selected}
              className={`w-full py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap ${
                selected
                  ? "bg-[#8b6f47] text-white cursor-pointer hover:bg-[#7a5f3a]"
                  : "bg-[#e8e0d5] text-[#b0a090] cursor-not-allowed"
              }`}
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              {currentQ < totalQ - 1 ? "下一題" : "查看結果"}
            </button>
          </div>
        )}

        {/* ── RESULT ── */}
        {phase === "result" && result && (
          <div className={`transition-opacity duration-300 ${animating ? "opacity-0" : "opacity-100"}`}>
            {/* Result hero image */}
            <div className="relative h-52 md:h-64 overflow-hidden rounded-t-2xl">
              <img
                src={result.img}
                alt={result.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-white/70 text-xs tracking-widest uppercase mb-1">你的能量飾品是</p>
                <h2
                  className="text-2xl md:text-3xl font-bold text-white"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {result.title}
                </h2>
                <p className="text-white/80 text-sm mt-1">{result.subtitle}</p>
              </div>
            </div>

            <div className="p-6 md:p-8">
              {/* Traits */}
              <div className="flex flex-wrap gap-2 mb-5">
                {result.traits.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ background: result.accent, color: result.color }}
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p
                className="text-[#5a4e45] text-sm leading-relaxed mb-6"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {result.desc}
              </p>

              {/* Recommended products */}
              <h4
                className="text-base font-bold text-[#3d3530] mb-3"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                為你推薦的商品
              </h4>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {recommendedProducts.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-xl overflow-hidden cursor-pointer group"
                    style={{ background: result.accent }}
                    onClick={handleViewProducts}
                  >
                    <div className="h-24 overflow-hidden">
                      <img
                        src={p.img}
                        alt={p.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2">
                      <p
                        className="text-xs font-medium text-[#3d3530] truncate"
                        style={{ fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {p.name}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: result.color }}>
                        NT$ {p.price.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleViewProducts}
                  className="flex-1 py-3.5 rounded-full text-sm font-semibold text-white cursor-pointer transition-all duration-300 whitespace-nowrap hover:opacity-90"
                  style={{ background: result.color, fontFamily: "'Noto Serif TC', serif" }}
                >
                  探索{result.title}系列
                </button>
                <button
                  onClick={handleRetake}
                  className="flex-1 py-3.5 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 whitespace-nowrap border-2"
                  style={{
                    borderColor: result.color,
                    color: result.color,
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  重新測驗
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
